/** ระยะเวลาที่บอทใช้ตอบ "กี่วันถึง/ทันไหม" — แยกกรณีมีของ/รอของ เก็บต่อแบรนด์ใน brand_settings */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { BrandSettingsApi, type Brand } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useBrands, brandLabel } from '@/lib/useBrands';

const FIELDS = [
  { key: 'leadProductionDays', label: '⏳ รอผลิต (กรณีรอของ)' },
  { key: 'leadEmbroideryDays', label: '🧵 ปัก' },
  { key: 'leadShippingDays', label: '🚚 จัดส่ง' },
] as const;
type Draft = Record<(typeof FIELDS)[number]['key'], string>;
const EMPTY: Draft = { leadProductionDays: '', leadEmbroideryDays: '', leadShippingDays: '' };

export default function LeadTimesCard() {
  const qc = useQueryClient();
  const { brands } = useBrands();
  const [brand, setBrand] = useState<Brand | ''>('');
  const active = brand || brands[0]?.brand_key || '';
  const { data } = useQuery({ queryKey: ['brand-settings', active], queryFn: () => BrandSettingsApi.list(active as Brand), enabled: !!active });
  const item = data?.[0];
  const [draft, setDraft] = useState<Draft>(EMPTY);
  useEffect(() => {
    setDraft({ leadProductionDays: item?.leadProductionDays ?? '', leadEmbroideryDays: item?.leadEmbroideryDays ?? '', leadShippingDays: item?.leadShippingDays ?? '' });
  }, [item]);
  const save = useMutation({
    mutationFn: () => BrandSettingsApi.update(item!.id, {
      leadProductionDays: draft.leadProductionDays.trim() || null,
      leadEmbroideryDays: draft.leadEmbroideryDays.trim() || null,
      leadShippingDays: draft.leadShippingDays.trim() || null,
    }),
    onSuccess: () => { toast.success('บันทึกแล้ว บอทใช้ค่าใหม่ทันที'); void qc.invalidateQueries({ queryKey: ['brand-settings'] }); },
    onError: () => toast.error('บันทึกไม่สำเร็จ'),
  });
  const dayText = (v: string) => (v.trim() ? `${v.trim()} วัน` : '');
  const ready = [dayText(draft.leadEmbroideryDays) && `ปัก ${dayText(draft.leadEmbroideryDays)}`, dayText(draft.leadShippingDays) && `จัดส่งอีก ${dayText(draft.leadShippingDays)}ถึง`].filter(Boolean).join(' + ');
  const wait = draft.leadProductionDays.trim() ? [`รอผลิต ${dayText(draft.leadProductionDays)}`, ready].filter(Boolean).join(' + ') : 'แอดมินจะแจ้งวันรอผลิตให้';

  return (
    <section className="border border-border rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">🗓️ ระยะเวลาผลิต ปัก และจัดส่ง</h2>
        <select className="border border-border rounded px-2 py-1 text-sm bg-background" value={active} onChange={(e) => setBrand(e.target.value as Brand)} aria-label="แบรนด์">
          {brands.map((b) => <option key={b.brand_key} value={b.brand_key}>{brandLabel(b.brand_key)}</option>)}
        </select>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {FIELDS.map((f) => (
          <label key={f.key} className="text-sm space-y-1 block">
            <span>{f.label} (วัน)</span>
            <Input value={draft[f.key]} placeholder="เช่น 10-14" onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
          </label>
        ))}
      </div>
      <div className="text-xs bg-muted/50 rounded p-2 space-y-0.5">
        <div className="font-medium">บอทจะตอบลูกค้าแบบนี้</div>
        <div>✅ มีของเลย: {ready || 'แอดมินจะแจ้งวันให้'}</div>
        <div>⏳ ต้องรอของ: {wait}</div>
      </div>
      <Button size="sm" disabled={!item || save.isPending} onClick={() => save.mutate()}><Save size={14} className="mr-1" />บันทึกระยะเวลา</Button>
      {active && !item && <p className="text-xs text-amber-700">แบรนด์นี้ยังไม่มี Brand Settings ต้องสร้างก่อน</p>}
    </section>
  );
}
